import { ElMessage } from 'element-plus';

import api from '@/api';
import { useStore } from '@/store';
import type { Store } from '@/store/types/actions';

interface ConsentExtra {
  email?: string;
  tel_code?: string;
  telephone?: string;
}

export interface UpdateConsentsOptions {
  sceneCode: string;
  extra?: ConsentExtra;
  page?: string;
  consentTypes?: number[];
  status?: number;
}

type CompliancePolicy = {
  consent_type?: number;
  id?: string | number | null;
  policy_id?: string | number | null;
  policy_version?: string | number;
};

type MarketingConsentItem = {
  consent_type: number;
  policy_id?: string | number;
  policy_version: string | number;
  email?: string;
  tel_code?: string;
  telephone?: string;
  status?: number;
};

export type UpdateConsentsResponse = {
  data?: {
    success?: boolean;
    message?: string;
  };
};

type ValidCompliancePolicy = CompliancePolicy & {
  consent_type: number;
  policy_version: string | number;
};

const isValidCompliancePolicy = (policy: CompliancePolicy): policy is ValidCompliancePolicy => {
  return typeof policy.consent_type === 'number' && policy.policy_version !== undefined;
};

export type UpdateConsentsRequestData = {
  scene_code: string;
  page?: string;
  items: MarketingConsentItem[];
};

export function buildComplianceConsentsPayload(
  policies: CompliancePolicy[] | undefined,
  options: UpdateConsentsOptions,
): UpdateConsentsRequestData | undefined {
  const { sceneCode, extra, page, consentTypes, status } = options;
  if (!policies?.length) return;

  const validPolicies = policies.filter(isValidCompliancePolicy);
  if (!validPolicies.length) return;

  const targetPolicies =
    consentTypes && consentTypes.length
      ? validPolicies.filter((policy) => consentTypes.includes(policy.consent_type))
      : validPolicies;
  if (!targetPolicies.length) return;

  const items = targetPolicies.map((policy) => {
    const policyId = policy.id ?? policy.policy_id;
    const item: MarketingConsentItem = {
      consent_type: policy.consent_type,
      policy_version: `${policy.policy_version}`,
    };
    if (policyId !== undefined && policyId !== null) {
      const normalizedPolicyId = Number(policyId);
      item.policy_id = Number.isFinite(normalizedPolicyId) ? normalizedPolicyId : policyId;
    }
    if (status !== undefined) {
      item.status = status;
    }
    if (policy.consent_type === 3 && extra?.email) {
      item.email = extra.email;
    }
    if ((policy.consent_type === 4 || policy.consent_type === 5) && extra?.tel_code) {
      item.tel_code = extra.tel_code;
    }
    if ((policy.consent_type === 4 || policy.consent_type === 5) && extra?.telephone) {
      item.telephone = extra.telephone;
    }
    return item;
  });

  return {
    scene_code: sceneCode,
    page: page || '',
    items,
  };
}

/**
 * 供 Pinia action 等非 setup 上下文调用；与 useComplianceConsent().updateConsentsAfterAuth 逻辑一致。
 */
export async function updateConsentsAfterAuthWithStore(
  store: Store,
  options: UpdateConsentsOptions,
): Promise<UpdateConsentsResponse | undefined> {
  try {
    const { sceneCode, extra, page, consentTypes, status } = options;
    const policies =
      (store.bffSsrData?.compliance_policies?.[sceneCode]?.policies as CompliancePolicy[]) || [];
    const payload = buildComplianceConsentsPayload(policies, {
      sceneCode,
      extra,
      page,
      consentTypes,
      status,
    });
    if (!payload) return;

    const res = (await api.commonTs.updateConsents(payload)) as UpdateConsentsResponse;
    const { data } = res;

    if (!data?.success && data?.message) {
      ElMessage.info(data.message);
    }

    const shouldRefreshMarketingStatus =
      data?.success !== false || ((payload.items?.length || 0) >= 2 && data?.success === false);
    if (shouldRefreshMarketingStatus) {
      await store.getMarketingStatus();
    }
    return res;
  } catch (error) {
    console.error(`@useComplianceConsent--updateConsentsAfterAuth ${options.sceneCode}`, error);
  }
}

export const useComplianceConsent = () => {
  /**
   * 登录/注册等认证成功后，根据 BFF 下发的合规策略自动上报协议同意状态
   * @param options.sceneCode 场景码，如 web.login / web.register
   * @param options.extra 补充字段：email（consent_type=3 必填）、tel_code / telephone（consent_type=4/5 必填）
   * @param options.page 页面中文名称，调用方负责传入（如 route.meta.cnName 或 this.$route.meta.cnName）
   * @param options.consentTypes 指定只上报部分协议类型；不传则上报当前场景全部有效协议
   * @param options.status 同意状态；不传则由接口默认处理
   */
  const updateConsentsAfterAuth = async (options: UpdateConsentsOptions) => {
    const store = useStore();
    return await updateConsentsAfterAuthWithStore(store, options);
  };

  return {
    updateConsentsAfterAuth,
  };
};
